<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    id="offcanvaRecipeForm"
    ref="offcanvaRecipeForm"
    data-bs-backdrop="static"
  >
    <div
      class="offcanvas-header py-2 d-flex align-items-center gap-2"
      style="background: linear-gradient(135deg, #E96C15 0%, #d46010 100%); color: #fff;"
    >
      <i class="fa-solid fa-mortar-pestle" />
      <span class="fw-semibold">{{ $t('Product Recipe') }}</span>
      <button
        type="button"
        style="font-size: small;"
        class="btn-close btn-close-white shadow-none ms-auto"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <!-- Product Info Header -->
      <div class="border-bottom px-3 py-3" style="background: #fafafa;">
        <div class="fw-bold mb-2" style="font-size: 17px;">
          {{ data.name ? $ln(data) : '' }}
        </div>
        <div class="d-flex flex-wrap align-items-center gap-2" style="font-size: 13px;">
          <span
            :class="['badge rounded-pill', data.active ? 'bg-success' : 'bg-danger']"
          >
            <i :class="['fa-solid me-1', data.active ? 'fa-circle-check' : 'fa-circle-xmark']" />
            {{ data.active ? $t('Active') : $t('Inactive') }}
          </span>
          <span
            :class="['badge rounded-pill', data.type == 'Configurale' ? 'bg-info text-dark' : 'bg-secondary text-white']"
          >
            <i class="fa-solid fa-tag me-1" />
            {{ data.type ? $t(data.type) : '' }}
          </span>
          <span class="text-muted" style="font-size: 12px;">
            <i class="fa-regular fa-clock me-1" />
            {{ formatDateTime(data.updated_at) }}
          </span>
        </div>
      </div>

      <!-- Simple Product (no config) -->
      <template v-if="!data.config">
        <div class="d-flex align-items-center px-3 pt-3 pb-2">
          <div style="width: 4px; height: 18px; background: #E96C15; border-radius: 2px;" class="me-2"></div>
          <span class="fw-semibold text-secondary small text-uppercase" style="letter-spacing: 0.04em;">
            <i class="fa-solid fa-flask me-1" />
            {{ $t('Recipe Ingredients & Costs') }}
          </span>
          <span class="badge bg-dark rounded-pill ms-2">{{ form.recipes.length }}</span>
        </div>
        <div class="px-3">
          <div class="overflow-x-auto">
            <table class="table table-sm table-hover align-middle mb-0">
              <thead style="font-size: 13px;">
                <tr style="background: #f5f5f5;">
                  <th
                    class="text-muted fw-medium"
                    style="min-width: 200px;"
                  >
                    {{ $t('Ingredient') }}
                  </th>
                  <th
                    class="text-center text-muted fw-medium"
                    style="min-width: 130px;"
                  >
                    {{ $t('System Quantity') }}
                    <popper
                      :placement="'top'"
                      :hover="true"
                      :arrow="true"
                      :disableClickAway="true"
                      class="popper-2 border-0 m-0"
                    >
                      <i
                        class="fa-solid fa-circle-info"
                        style="color: #E96C15;"
                        data-bs-toggle="popover"
                        data-bs-placement="top"
                      />
                      <template #content>
                        <div>{{ $t('Affects inventory and cost.') }}</div>
                      </template>
                    </popper>
                  </th>
                  <th
                    class="text-center text-muted fw-medium"
                    style="min-width: 130px;"
                  >
                    {{ $t('Display Quantity') }}
                    <popper
                      :placement="'top'"
                      :hover="true"
                      :arrow="true"
                      :disableClickAway="true"
                      class="popper-2 border-0 m-0"
                    >
                      <i
                        class="fa-solid fa-circle-info"
                        style="color: #E96C15;"
                        data-bs-toggle="popover"
                        data-bs-placement="top"
                      />
                      <template #content>
                        <div>{{ $t('What customers or staff see on the POS screen.') }}</div>
                      </template>
                    </popper>
                  </th>
                  <th
                    class="text-muted fw-medium"
                    style="min-width: 80px;"
                  >
                    {{ $t('Unit') }}
                  </th>
                  <th
                    class="text-muted fw-medium"
                    style="min-width: 100px; max-width: 100px; width: 100px;"
                  >
                    {{ $t('Cost') }}
                  </th>
                  <th
                    class="text-muted fw-medium"
                    style="min-width: 120px; max-width: 120px; width: 120px;"
                  >
                    {{ $t('Condition') }}
                  </th>
                  <th
                    class="text-end text-muted fw-medium"
                    style="min-width: 100px; max-width: 100px; width: 100px;"
                  >
                    {{ $t('Action') }}
                  </th>
                </tr>
              </thead>
              <tbody class="align-baseline" style="font-size: 13px;">
                <tr
                  v-for="(recipe, index) in form.recipes"
                  :key="index"
                >
                  <item-recipe-form
                    :index="index"
                    :recipe="recipe"
                    :select2-condition-options="select2ConditionOptions"
                  />
                </tr>
                <tr v-if="form.recipes.length == 0">
                  <td
                    colspan="7"
                    class="text-center text-muted py-4"
                  >
                    <i class="fa-solid fa-flask mb-2" style="font-size: 24px; opacity: 0.4;" /><br>
                    {{ $t('No Available Data') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex align-items-center justify-content-between mt-2 mb-1 flex-wrap gap-2">
            <div class="d-flex align-items-center gap-2" style="min-width: 280px; max-width: 350px; flex: 1;">
              <i class="fa-solid fa-plus text-muted" style="font-size: 12px;" />
              <select2
                v-model="selectInventory"
                @select="onSelectedInventory"
                :options="select2Items.options"
                :settings="select2Items.setting"
                :placeholder="$t('Add Ingredient') + '...'"
                style="flex: 1;"
              />
            </div>
            <div
              class="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
              style="background: #fff3e8; border: 1px solid #f0d4b8;"
            >
              <span class="text-muted" style="font-size: 13px;">
                {{ $t('Total Estimated Cost') }}
              </span>
              <span class="fw-bold" style="font-size: 15px; color: #E96C15;">
                {{ formatNumberTwoDigit(costEstimate) }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <!-- Configurable Product (with variants) -->
      <template v-else>
        <!-- Variant Tabs -->
        <div class="border-bottom px-3 py-3" style="background: #fff;">
          <div class="d-flex align-items-center mb-2">
            <div style="width: 4px; height: 18px; background: #E96C15; border-radius: 2px;" class="me-2"></div>
            <span class="fw-semibold text-secondary small text-uppercase" style="letter-spacing: 0.04em;">
              <i class="fa-solid fa-code-branch me-1" />
              {{ $t('Variants') }}
            </span>
          </div>
          <div
            class="d-flex flex-nowrap gap-2 pb-1"
            style="overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: thin;"
          >
            <button
              type="button"
              class="btn btn-sm d-flex align-items-center gap-1 flex-shrink-0 shadow-none"
              :class="selectedVariantId === 'all' ? 'text-white' : 'btn-outline-secondary'"
              :style="selectedVariantId === 'all'
                ? 'background: #E96C15; border-color: #E96C15; font-size: 13px;'
                : 'font-size: 13px; border-style: dashed;'"
              @click="selectedVariantId = 'all'"
            >
              <i class="fa-solid fa-layer-group" />
              {{ $t('All Variants') }}
            </button>
            <button
              v-for="variant in form.variants"
              :key="variant.id"
              type="button"
              class="btn btn-sm d-flex align-items-center gap-1 flex-shrink-0 shadow-none"
              :class="selectedVariantId == variant.id ? 'text-white' : 'btn-outline-secondary'"
              :style="selectedVariantId == variant.id
                ? 'background: #E96C15; border-color: #E96C15; font-size: 13px;'
                : 'font-size: 13px;'"
              @click="selectedVariantId = variant.id"
            >
              {{ variant.name ? $ln(variant) : '' }}
              <span
                v-if="variant.recipes.length"
                class="badge rounded-pill"
                :style="selectedVariantId == variant.id
                  ? 'background: rgba(255,255,255,0.25); font-size: 10px;'
                  : 'background: #e9ecef; color: #6c757d; font-size: 10px;'"
              >
                {{ variant.recipes.length }}
              </span>
            </button>
          </div>
        </div>

        <!-- All Variants Bulk Add Mode -->
        <template v-if="selectedVariantId === 'all'">
          <div class="d-flex align-items-center px-3 pt-3 pb-2">
            <div style="width: 4px; height: 18px; background: #E96C15; border-radius: 2px;" class="me-2"></div>
            <span class="fw-semibold text-secondary small text-uppercase" style="letter-spacing: 0.04em;">
              <i class="fa-solid fa-layer-group me-1" />
              {{ $t('Add Ingredient to All Variants') }}
            </span>
            <span
              v-if="bulkPending.length"
              class="badge bg-dark rounded-pill ms-2"
            >{{ bulkPending.length }}</span>
          </div>
          <div class="px-3">
            <div
              class="d-flex align-items-start gap-2 py-2 px-3 mb-3 rounded-3"
              style="background: #fff3e8; border: 1px solid #f0d4b8; color: #8a5a2b; font-size: 12px;"
            >
              <i class="fa-solid fa-circle-info mt-1" />
              <div>
                {{ $t('Ingredient will be added only to variants that do not already have it. Existing recipes will not be changed.') }}
              </div>
            </div>

            <div class="overflow-x-auto" v-if="bulkPending.length">
              <table class="table table-sm table-hover align-middle mb-0">
                <thead style="font-size: 13px;">
                  <tr style="background: #f5f5f5;">
                    <th class="text-muted fw-medium" style="min-width: 200px;">
                      {{ $t('Ingredient') }}
                    </th>
                    <th class="text-center text-muted fw-medium" style="min-width: 130px;">
                      {{ $t('System Quantity') }}
                    </th>
                    <th class="text-center text-muted fw-medium" style="min-width: 130px;">
                      {{ $t('Display Quantity') }}
                    </th>
                    <th class="text-muted fw-medium" style="min-width: 80px;">
                      {{ $t('Unit') }}
                    </th>
                    <th class="text-muted fw-medium" style="min-width: 120px;">
                      {{ $t('Condition') }}
                    </th>
                    <th class="text-end text-muted fw-medium" style="min-width: 80px;">
                      {{ $t('Action') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="align-baseline" style="font-size: 13px;">
                  <tr v-for="(item, idx) in bulkPending" :key="item.inventory_id">
                    <td>{{ item.name }}</td>
                    <td>
                      <input
                        type="number"
                        v-model.number="item.quantity_system"
                        inputmode="decimal"
                        class="form-control form-control-sm shadow-none text-end"
                        style="font-size: 12px;"
                        :placeholder="$t('Enter system quantity') + '...'"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        v-model.number="item.quantity_display"
                        inputmode="decimal"
                        class="form-control form-control-sm shadow-none text-end"
                        style="font-size: 12px;"
                        :placeholder="$t('Enter display quantity') + '...'"
                      />
                    </td>
                    <td>{{ item.unit_name ? $t(item.unit_name) : '' }}</td>
                    <td>
                      <select2
                        v-model.trim="item.condition"
                        :options="select2ConditionOptions"
                        :settings="{ minimumResultsForSearch: Infinity }"
                      />
                    </td>
                    <td class="align-content-center text-end">
                      <button
                        type="button"
                        style="font-size: 9px;"
                        class="btn btn-icon btn-outline-danger btn-sm"
                        @click="removeBulkPending(idx)"
                      >
                        <i class="fa-solid fa-trash-can" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-muted py-4">
              <i class="fa-solid fa-flask mb-2" style="font-size: 24px; opacity: 0.4;" /><br>
              {{ $t('Select an ingredient to add to the queue') }}
            </div>

            <div class="d-flex align-items-center justify-content-between mt-2 mb-1 flex-wrap gap-2">
              <div class="d-flex align-items-center gap-2" style="min-width: 280px; max-width: 350px; flex: 1;">
                <i class="fa-solid fa-plus text-muted" style="font-size: 12px;" />
                <select2
                  v-model="bulkSelectInventory"
                  @select="onBulkSelectInventory"
                  :options="select2Items.options"
                  :settings="select2Items.setting"
                  :placeholder="$t('Add Ingredient') + '...'"
                  style="flex: 1;"
                />
              </div>
              <button
                type="button"
                class="btn btn-sm text-white shadow-none d-flex align-items-center gap-1"
                style="background: #E96C15; border-color: #E96C15;"
                :disabled="!bulkPending.length"
                @click="applyBulkToAllVariants"
              >
                <i class="fa-solid fa-layer-group" />
                {{ $t('Apply to all variants') }}
                <span
                  v-if="bulkPending.length"
                  class="badge rounded-pill bg-white"
                  style="color: #E96C15; font-size: 10px;"
                >{{ bulkPending.length }}</span>
              </button>
            </div>

            <div v-if="bulkAddHistory.length" class="mt-4">
              <div class="text-muted small fw-medium mb-2 text-uppercase" style="letter-spacing: 0.04em;">
                <i class="fa-solid fa-clock-rotate-left me-1" />
                {{ $t('Recently Added') }}
              </div>
              <div
                v-for="(item, i) in bulkAddHistory"
                :key="i"
                class="d-flex justify-content-between align-items-center py-2 border-bottom"
                style="font-size: 13px;"
              >
                <span>
                  <i class="fa-solid fa-circle-check text-success me-1" />
                  {{ item.name }}
                  <span class="text-muted">× {{ formatDigit(item.quantity_system) }} {{ item.unit_name ? $t(item.unit_name) : '' }}</span>
                </span>
                <span
                  class="badge rounded-pill"
                  style="background: #fff3e8; color: #E96C15; font-size: 11px;"
                >
                  {{ item.appliedCount }}/{{ item.totalVariants }} {{ $t('variants') }}
                </span>
              </div>
            </div>
          </div>
        </template>

        <!-- Selected Variant Recipe Content -->
        <template v-else-if="selectedVariantId && selectedVariant">
          <div class="d-flex align-items-center px-3 pt-3 pb-2">
            <div style="width: 4px; height: 18px; background: #E96C15; border-radius: 2px;" class="me-2"></div>
            <span class="fw-semibold text-secondary small text-uppercase" style="letter-spacing: 0.04em;">
              <i class="fa-solid fa-flask me-1" />
              {{ $t('Recipe Ingredients & Costs') }}
            </span>
            <span class="badge bg-dark rounded-pill ms-2">{{ selectedVariant.recipes.length }}</span>
          </div>
          <div class="px-3">
            <div class="overflow-x-auto">
              <table class="table table-sm table-hover align-middle mb-0">
                <thead style="font-size: 13px;">
                  <tr style="background: #f5f5f5;">
                    <th
                      class="text-muted fw-medium"
                      style="min-width: 200px;"
                    >
                      {{ $t('Ingredient') }}
                    </th>
                    <th
                      class="text-center text-muted fw-medium"
                      style="min-width: 130px;"
                    >
                      {{ $t('System Quantity') }}
                      <popper
                        :placement="'top'"
                        :hover="true"
                        :arrow="true"
                        :disableClickAway="true"
                        class="popper-2 border-0 m-0"
                      >
                        <i
                          class="fa-solid fa-circle-info"
                          style="color: #E96C15;"
                          data-bs-toggle="popover"
                          data-bs-placement="top"
                        />
                        <template #content>
                          <div>{{ $t('Affects inventory and cost.') }}</div>
                        </template>
                      </popper>
                    </th>
                    <th
                      class="text-center text-muted fw-medium"
                      style="min-width: 130px;"
                    >
                      {{ $t('Display Quantity') }}
                      <popper
                        :placement="'top'"
                        :hover="true"
                        :arrow="true"
                        :disableClickAway="true"
                        class="popper-2 border-0 m-0"
                      >
                        <i
                          class="fa-solid fa-circle-info"
                          style="color: #E96C15;"
                          data-bs-toggle="popover"
                          data-bs-placement="top"
                        />
                        <template #content>
                          <div>{{ $t('What customers or staff see on the POS screen.') }}</div>
                        </template>
                      </popper>
                    </th>
                    <th
                      class="text-muted fw-medium"
                      style="min-width: 80px;"
                    >
                      {{ $t('Unit') }}
                    </th>
                    <th
                      class="text-muted fw-medium"
                      style="min-width: 100px; max-width: 100px; width: 100px;"
                    >
                      {{ $t('Cost') }}
                    </th>
                    <th
                      class="text-muted fw-medium"
                      style="min-width: 120px; max-width: 120px; width: 120px;"
                    >
                      {{ $t('Condition') }}
                    </th>
                    <th
                      class="text-end text-muted fw-medium"
                      style="min-width: 100px; max-width: 100px; width: 100px;"
                    >
                      {{ $t('Action') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="align-baseline" style="font-size: 13px;">
                  <tr
                    v-for="(recipe, index) in selectedVariant.recipes"
                    :key="index"
                  >
                    <item-recipe-form
                      :index="index"
                      :recipe="recipe"
                      :select2-condition-options="select2ConditionOptions"
                    />
                  </tr>
                  <tr v-if="selectedVariant.recipes.length == 0">
                    <td
                      colspan="7"
                      class="text-center text-muted py-4"
                    >
                      <i class="fa-solid fa-flask mb-2" style="font-size: 24px; opacity: 0.4;" /><br>
                      {{ $t('No Available Data') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="d-flex align-items-center justify-content-between mt-2 mb-1 flex-wrap gap-2">
              <div class="d-flex align-items-center gap-2" style="min-width: 280px; max-width: 350px; flex: 1;">
                <i class="fa-solid fa-plus text-muted" style="font-size: 12px;" />
                <select2
                  v-model="selectInventory"
                  @select="onSelectedInventory"
                  :options="select2Items.options"
                  :settings="select2Items.setting"
                  :placeholder="$t('Add Ingredient') + '...'"
                  style="flex: 1;"
                />
              </div>
              <div
                class="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
                style="background: #fff3e8; border: 1px solid #f0d4b8;"
              >
                <span class="text-muted" style="font-size: 13px;">
                  {{ $t('Total Estimated Cost') }}
                </span>
                <span class="fw-bold" style="font-size: 15px; color: #E96C15;">
                  {{ formatNumberTwoDigit(selectedVariantCostEstimate) }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div
      class="border-top d-flex justify-content-end align-items-center gap-2 px-3 py-2"
      style="background: #fafafa;"
      v-if="!loading && hasPermission"
    >
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="btn btn-sm btn-light border"
      >
        {{ $t('Cancel') }}
      </button>
      <ladda
        text="Save"
        :btn-class="'btn btn-sm text-white'"
        :style="'background: #E96C15; border-color: #E96C15;'"
        :loading="loadingSave"
        :disabled="v$.$invalid"
        @click="handleUpdate"
      />
    </div>
  </div>
</template>

<script lang="js">
import AccessDenied from "@/plugins/AccessDenied.vue";
import { Products } from '@/services/endpoint/products.service';
import { AUTH, API_URLS } from '@/services/endpoint/_constants';
import signature  from '@/services/signature';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import Ladda from '@/plugins/Ladda.vue';
import moment from "moment";
import numeral from 'numeral';
import { distance } from '@/store/date-fns'
import Popper from "vue3-popper";
import Select2 from 'vue3-select2-component';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation-recipe";
import ItemRecipeForm from "./ItemRecipeForm.vue";

export default {
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return validation(this.form);
  },
  props: {
    id: {
      type: Number,
      default: 0
    }
  },
  data() {
    let vm = this;
    return {
      loadingSave: false,
      hasPermission: false,
      selectedVariantId: '',
      loading: true,
      select2ConditionOptions: [],
      data: {},
      form: {
        cost_estimate: '',
        is_config: '',
        recipes: [],
        variants: []
      },
      selectInventory: [],
      bulkSelectInventory: [],
      bulkPending: [],
      bulkAddHistory: [],
      select2Items: {
        options: [],
        setting: {
          dropdownParent: '#offcanvaRecipeForm',
          minimumResultsForSearch: Infinity,
          multiple: true,
          language: {
            loadingMore: function (e) {
              return '<i class="fa-solid fa-spinner fa-spin" />'
            }
          },
          ajax: {
            beforeSend: function (request) {
              request.setRequestHeader('Authorization', 'Bearer ' + AUTH.TOKEN);
            },
            url: AUTH.URL + API_URLS.API_PRODUCT_ITEM_RECECIPE,
            data: function (params) {
              var query = {
                search: params.term,
                page: params.page || 1,
                limit: 10
              };
              return signature($.param(query)).substring(1);
            },
            processResults: function (data, params) {
              let option = data.data;
              let pageCurrent = data.meta.current_page;
              option = option.map(function(item) {
                let disabled = false;
                if (vm.form.is_config && vm.selectedVariantId === 'all') {
                  const inPending = vm.bulkPending.find(p => p.inventory_id == item.id);
                  const allHave = vm.form.variants.length > 0
                    && vm.form.variants.every(v => v.recipes.find(r => r.inventory_id == item.id));
                  disabled = !!inPending || allHave;
                } else if (vm.form.is_config && vm.selectedVariantId) {
                  const variant = vm.form.variants.find(v => v.id == vm.selectedVariantId);
                  const recipes = variant ? variant.recipes : [];
                  disabled = recipes.find((inventory) => inventory.inventory_id == item.id) ? true : false;
                } else {
                  disabled = vm.form.recipes.find((inventory) => inventory.inventory_id == item.id) ? true : false;
                }
                return {
                  id: item.id,
                  text: item.name,
                  cost: item.cost,
                  unit_id: item.unit_id,
                  unit_name: item.unit_name,
                  unit_symbol: item.unit_symbol,
                  disabled: disabled
                }
              });
              return {
                results: option,
                pagination: {
                  more: (pageCurrent * 10) < data.meta.total
                }
              }
            }
          }
        }
      },
    }
  },
  components: {
    AccessDenied,
    PlaceholderContent,
    Ladda,
    Popper,
    Select2,
    ItemRecipeForm
  },
  methods: {
    async deleteItem(index) {
      if (this.form.is_config && this.selectedVariantId && this.selectedVariantId !== 'all') {
        const variant = this.form.variants.find(v => v.id == this.selectedVariantId);
        if (variant) variant.recipes.splice(index, 1);
      } else {
        this.form.recipes.splice(index, 1);
      }
    },
    async onSelectedInventory(e) {
      this.selectInventory = [];
      if (!e || !e.id) return;
      let recipes;
      if (this.form.is_config && this.selectedVariantId && this.selectedVariantId !== 'all') {
        const variant = this.form.variants.find(v => v.id == this.selectedVariantId);
        recipes = variant.recipes;
      } else {
        recipes = this.form.recipes;
      }
      if (recipes.find((item) => item.inventory_id == e.id)) return;
      recipes.push({
        inventory_id: e.id,
        name: e.text,
        cost: e.cost,
        unit_id: e.unit_id,
        unit_name: e.unit_name,
        unit_symbol: e.unit_symbol,
        quantity_system: '',
        quantity_display: '',
        total_cost: 0,
        condition: this.select2ConditionOptions[0].id
      });
      this.select2Items.options = [];
    },
    onBulkSelectInventory(e) {
      this.bulkSelectInventory = [];
      if (!e || !e.id) return;
      if (this.bulkPending.find(p => p.inventory_id == e.id)) {
        this.select2Items.options = [];
        return;
      }
      this.bulkPending.push({
        inventory_id: e.id,
        name: e.text,
        cost: e.cost,
        unit_id: e.unit_id,
        unit_name: e.unit_name,
        unit_symbol: e.unit_symbol,
        quantity_system: '',
        quantity_display: '',
        condition: this.select2ConditionOptions[0] ? this.select2ConditionOptions[0].id : ''
      });
      this.select2Items.options = [];
    },
    removeBulkPending(index) {
      this.bulkPending.splice(index, 1);
    },
    applyBulkToAllVariants() {
      if (!this.bulkPending.length) return;
      const totalVariants = this.form.variants.length;
      let totalApplied = 0;
      const historyEntries = [];
      this.bulkPending.forEach(item => {
        const qtySystem = item.quantity_system;
        const qtyDisplay = item.quantity_display;
        const unitCost = item.cost || 0;
        const totalCost = ((qtySystem || 0) * unitCost) || 0;
        let appliedCount = 0;
        this.form.variants.forEach(variant => {
          const exists = variant.recipes.find(r => r.inventory_id == item.inventory_id);
          if (exists) return;
          variant.recipes.push({
            inventory_id: item.inventory_id,
            name: item.name,
            cost: item.cost,
            unit_id: item.unit_id,
            unit_name: item.unit_name,
            unit_symbol: item.unit_symbol,
            quantity_system: qtySystem === '' ? '' : qtySystem,
            quantity_display: qtyDisplay === '' ? '' : qtyDisplay,
            total_cost: totalCost,
            condition: item.condition
          });
          appliedCount++;
        });
        totalApplied += appliedCount;
        historyEntries.push({
          name: item.name,
          quantity_system: qtySystem,
          unit_name: item.unit_name,
          appliedCount,
          totalVariants
        });
      });
      this.bulkAddHistory = [...historyEntries, ...this.bulkAddHistory].slice(0, 10);
      if (totalApplied > 0) {
        this.$toast.success(
          "<B>" + this.$t('Success') + "</B>\n"
          + this.$t('Applied {ingredients} ingredient(s) to variants', { ingredients: this.bulkPending.length })
        );
      } else {
        this.$toast.info(this.$t('All variants already have these ingredients'));
      }
      this.bulkPending = [];
    },
    formatDigit(value) {
      if ((value < 0.01) && value != 0) {
        return numeral(value).format("0,0.0000");
      }
      return numeral(value).format("0,0.00");
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.0000");
    },
    formatNumberTwoDigit(value) {
      return numeral(value).format("0,0.00");
    },
    async getInfoData() {
      this.loading = true;
      let vm = this;
      await Products.getUpdateRecipe(this.id).then(res => {
        this.data = res.data.data;

        this.form.cost_estimate = res.data.data.cost;
        this.form.is_config = res.data.data.config;
        this.form.recipes = res.data.data.recipes.map(function(recipe) {
          return {
            id: recipe.id,
            inventory_id: recipe.inventory_id,
            name: recipe.inventory_name,
            quantity_system: recipe.quantity_system,
            quantity_display: recipe.quantity_display,
            cost: recipe.cost,
            total_cost: (recipe.quantity_system * recipe.cost) || 0,
            condition: recipe.condition,
            unit_id: recipe.unit_id,
            unit_name: recipe.unit_name
          }
        });
        this.form.variants = res.data.data.variants.map(function(variant) {
          return {
            id: variant.id,
            cost_estimate: variant.cost,
            image_url: variant.image_url,
            thumbnail_url: variant.thumbnail_url,
            name: variant.name,
            recipes: variant.recipes.map(function(recipe) {
              return {
                id: recipe.id,
                inventory_id: recipe.inventory_id,
                name: recipe.inventory_name,
                quantity_system: recipe.quantity_system,
                quantity_display: recipe.quantity_display,
                cost: recipe.cost,
                total_cost: (recipe.quantity_system * recipe.cost) || 0,
                condition: recipe.condition,
                unit_id: recipe.unit_id,
                unit_name: recipe.unit_name,
                unit_symbol: recipe.unit_symbol
              }
            }),
          }
        });
        this.form.cost_estimate = res.data.data.cost;
        this.select2ConditionOptions = res.data.filter.conditions.map(function(condition) {
          return {
            id: condition.value,
            text: condition.text ? vm.$t(condition.text) : ''
          }
        });
        if (this.form.variants.length > 0) {
          this.selectedVariantId = 'all';
        }
        this.hasPermission = true;
        this.loading = false;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
        this.loading = false;
			  throw err;
      });
    },
    setParamsBeforeSendUpdate() {
      let params = _.clone(this.form);
      if (params.is_config) {
        delete params.recipes;
      } else {
        delete params.variants;
      }
      return $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    async handleUpdate() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      let params = _.clone(this.form);
      if (params.is_config) {
        delete params.recipes;
      } else {
        delete params.variants;
      }
      await Products.updateRecipe(this.id, params).then(res => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Success!'));
        $(this.$refs.offcanvaRecipeForm).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.errors) {
          this.errors = err?.response?.data?.errors;
        }
        this.loadingSave = false;
      });
    },
    formatDate(value) {
      return moment(value).format('DD/MM/YYYY');
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    dateDistance(value) {
      return distance(value);
    },
  },
  async mounted() {
    $(this.$refs.offcanvaRecipeForm).offcanvas('show');

    $(this.$refs.offcanvaRecipeForm).on('shown.bs.offcanvas',async () => {
      try {
        await this.getInfoData();
      } catch (err) {
      }
    });

    $(this.$refs.offcanvaRecipeForm).on('hidden.bs.offcanvas', () => {
      this.$emit('close');
    });
  },
  computed: {
    selectedVariant() {
      if (!this.selectedVariantId) return null;
      return this.form.variants.find(v => v.id == this.selectedVariantId) || null;
    },
    costEstimate() {
      return this.form.recipes.reduce((sum, value) => {
        return sum + (value.total_cost * 1);
      }, 0);
    },
    selectedVariantCostEstimate() {
      if (!this.selectedVariant) return 0;
      return this.selectedVariant.recipes.reduce((sum, value) => {
        return sum + ((value.total_cost * 1) || 0);
      }, 0);
    }
  },
  watch: {
    costEstimate(nval) {
      this.form.cost_estimate = nval;
    },
    selectedVariantCostEstimate(nval) {
      if (this.selectedVariant) {
        this.selectedVariant.cost_estimate = nval;
      }
    }
  }
}
</script>
